import { Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import ExampleDialog from "../../components/dialog/ExampleDialog";
import NavigationDrawer from "../../components/drawer/NavigationDrawer";
import { NAVIGATION_DRAWER_TYPE_EXAMPLE } from "../../components/drawer/NavigationDrawer/NavigationDrawer";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { EDIT_PERMISSION } from "../../helpers/constants";
import { useAsync } from "../../hooks/useAsync";
import useDialogWithId from "../../hooks/useDialogWithId";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import useCheckPermission from "../../hooks/usePermission";
import useFooService from "../../services/fooService";
import TableService from "../../services/tableService";
import { EXAMPLE_TABLE_FIELDS_CONFIG, EXAMPLE_TABLE_NAME } from "./PageTablesConfig";
const TABLE_CONFIGS = [{ name: EXAMPLE_TABLE_NAME, config: EXAMPLE_TABLE_FIELDS_CONFIG }];

const EXAMPLE_DATA = [
    {
        id: 1,
        foo_number: 123,
        product: "foo product",
        foo_date: "2025-01-01",
        foo_value: 123.45,
    },
];

export default function ExamplePage(props) {
    const { pageName } = props;

    const { t } = useTranslation();
    // check permission if is required see useCheckPermission hook
    // const [hasEditPermission] = useCheckPermission(EDIT_PERMISSION)
    const [hasEditPermission] = [true];

    const [dataLocal, setDataLocal] = useState(EXAMPLE_DATA);
    const [countFooRecord, setCountFooRecord] = useState();
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    const [openExampleDialog, exampleId, onOpenExampleDialog, onCloseExampleDialog] =
        useDialogWithId();

    const {
        resetPageNumber,
        searchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { getFooListDataWithSearchParams } = useFooService();

    const fooData = useAsync(() => getFooListDataWithSearchParams(searchParams), [searchParams]);

    const fooFilteringData = useAsync(() => Promise.resolve({}));

    useEffect(() => {
        if (fooData.loading) {
            return;
        }
        if (fooData.value.results !== undefined) {
            setDataLocal(fooData.value.results);
            setCountFooRecord(fooData.value.count);
        }
    }, [fooData.loading]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
        onOpenExampleDialog(id);
    }, []);

    const closeDialog = useCallback(() => {
        setSelectedRowId(undefined);
        setCheckedAll(false);
        onCloseExampleDialog();
        fooData.refetch();
    }, [selectedRowId]);

    const isFooTableLoading = false; // setLoading when data are preparing

    const fooDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            EXAMPLE_TABLE_FIELDS_CONFIG,
            hiddenColumnsForTables?.[EXAMPLE_TABLE_NAME],
            columnsOrdersForTables?.[EXAMPLE_TABLE_NAME],
        );
    }, [
        hiddenColumnsForTables?.[EXAMPLE_TABLE_NAME],
        columnsOrdersForTables?.[EXAMPLE_TABLE_NAME],
        EXAMPLE_TABLE_FIELDS_CONFIG,
    ]);

    const data = React.useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                EXAMPLE_TABLE_FIELDS_CONFIG,
                dataLocal,
                hiddenColumnsForTables?.[EXAMPLE_TABLE_NAME]
                    ? hiddenColumnsForTables?.[EXAMPLE_TABLE_NAME]
                    : [],
                columnsOrdersForTables?.[EXAMPLE_TABLE_NAME]
                    ? columnsOrdersForTables?.[EXAMPLE_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            EXAMPLE_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    return (
        <DefaultPageWrapper titleKey={"example"}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        filteringData={fooFilteringData ? fooFilteringData : []}
                        refreshTable={fooData.refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"example"}
                        style={{ marginBlock: "5px" }}
                        onClickCreateItem={() => onOpenExampleDialog()}
                        showCreateButton={hasEditPermission}
                        mdGridButton={hasEditPermission ? 1.25 : 2.75}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isFooTableLoading}>
                        <FilterTable
                            data={data}
                            countRecords={countFooRecord}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                                EXAMPLE_TABLE_FIELDS_CONFIG,
                                fooFilteringData ? fooFilteringData : [],
                                hiddenColumnsForTables?.[EXAMPLE_TABLE_NAME]
                                    ? hiddenColumnsForTables?.[EXAMPLE_TABLE_NAME]
                                    : [],
                                columnsOrdersForTables?.[EXAMPLE_TABLE_NAME]
                                    ? columnsOrdersForTables?.[EXAMPLE_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"mock url"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={fooDataExportHeaders}
                            exportToFileFileName={`${t(
                                "page.ico_regitry_page.loans_and_bonds",
                            ).replace(" ", "_")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={true}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
            {openExampleDialog && (
                <ExampleDialog
                    open={openExampleDialog}
                    onClose={closeDialog}
                    onRefetchData={fooData.refetch}
                    fooId={exampleId}
                    readOnly={false}
                />
            )}
        </DefaultPageWrapper>
    );
}
